// Firestore-backed Staff Meeting Service
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';

export interface StaffMeetingData {
  id: string;
  practiceOwnerId: string;
  title: string;
  description: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  type: 'video' | 'in-person' | 'phone';
  location: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  attendeeIds: string[];
  attendeeNames: string[];
  agenda: string;
  notes: string;
  recurring: 'none' | 'weekly' | 'biweekly' | 'monthly';
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
}

export type CreateMeetingRequest = Omit<StaffMeetingData, 'id' | 'createdAt' | 'updatedAt' | 'status'>;

class FirebaseStaffMeetingService {
  private static instance: FirebaseStaffMeetingService;
  private collectionName = 'staff_meetings';

  static getInstance(): FirebaseStaffMeetingService {
    if (!FirebaseStaffMeetingService.instance) {
      FirebaseStaffMeetingService.instance = new FirebaseStaffMeetingService();
    }
    return FirebaseStaffMeetingService.instance;
  }

  async createMeeting(data: CreateMeetingRequest): Promise<string> {
    const docRef = await addDoc(collection(db, this.collectionName), {
      ...data,
      status: 'scheduled',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  }

  async updateMeeting(meetingId: string, updates: Partial<StaffMeetingData>): Promise<void> {
    const docRef = doc(db, this.collectionName, meetingId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  async deleteMeeting(meetingId: string): Promise<void> {
    await deleteDoc(doc(db, this.collectionName, meetingId));
  }

  async completeMeeting(meetingId: string, notes?: string): Promise<void> {
    const updates: any = { status: 'completed', updatedAt: serverTimestamp() };
    if (notes) updates.notes = notes;
    await updateDoc(doc(db, this.collectionName, meetingId), updates);
  }

  async cancelMeeting(meetingId: string): Promise<void> {
    await updateDoc(doc(db, this.collectionName, meetingId), {
      status: 'cancelled',
      updatedAt: serverTimestamp()
    });
  }

  subscribeToMeetings(
    practiceOwnerId: string,
    callback: (meetings: StaffMeetingData[]) => void
  ): () => void {
    const q = query(
      collection(db, this.collectionName),
      where('practiceOwnerId', '==', practiceOwnerId),
      orderBy('date', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
      const meetings = snapshot.docs.map((d) => this.convert(d.id, d.data()));
      callback(meetings);
    }, (error) => {
      console.error('[StaffMeetingService] Subscription error:', error);
      callback([]);
    });
  }

  async getMeetingsForDateRange(
    practiceOwnerId: string,
    startDate: string,
    endDate: string
  ): Promise<StaffMeetingData[]> {
    const q = query(
      collection(db, this.collectionName),
      where('practiceOwnerId', '==', practiceOwnerId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => this.convert(d.id, d.data()));
  }

  private convert(id: string, data: any): StaffMeetingData {
    return {
      id,
      practiceOwnerId: data.practiceOwnerId || '',
      title: data.title || '',
      description: data.description || '',
      date: data.date || '',
      startTime: data.startTime || '',
      endTime: data.endTime || '',
      type: data.type || 'video',
      location: data.location || '',
      status: data.status || 'scheduled',
      attendeeIds: data.attendeeIds || [],
      attendeeNames: data.attendeeNames || [],
      agenda: data.agenda || '',
      notes: data.notes || '',
      recurring: data.recurring || 'none',
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
    };
  }
}

export default FirebaseStaffMeetingService;
